import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FaPencilAlt, FaTimes, FaEnvelope, FaUser, FaKey } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';

import Wrapper from '../../components/Wrapper';
import axiosInstance from '../../utils/axios';
import '../../styles/profile.css';
import { updateSupportProfileSuccess } from '../../store/features/supportSlice';

function SupportProfile() {
  
  const { support } = useSelector((state) => state.support);
  const superAdminId = support?.id;

  const [adminData, setAdminData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [editInfo, setEditInfo] = useState(false);

  // Edit form states
  const [formData, setFormData] = useState({
    name: '',
    password: ''
  });

  const [updating, setUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState({ type: '', text: '' });

  // FETCH DATA LOGIC
  useEffect(() => {
    const fetchData = async () => {
      if (!superAdminId) return;
      
      setLoadingData(true);
      setUpdateMessage({ type: '', text: '' });

      try {
        const res = await axiosInstance.get(`/super-admin/getSuperAdmin/${superAdminId}`, {
        });

        if (res.data) {
          setAdminData(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch admin info", err);
        setUpdateMessage({
          type: 'danger',
          text: 'Failed to fetch user data.'
        });
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [superAdminId]);

  // Initialize Form Data
  useEffect(() => {
    if (adminData && editInfo) {
      setFormData({
        name: adminData.name || '',
        password: '' 
      });
    }
  }, [adminData, editInfo]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const dispatch = useDispatch();

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setUpdateMessage({ type: '', text: '' });

    try {
      const payload = {
        super_admin_id: superAdminId,
        name: formData.name,
        password: formData.password
      };

      const res = await axiosInstance.put(`/super-admin/updateProfile/${superAdminId}`, payload);

      if (res.data.success) {
        setUpdateMessage({ type: 'success', text: 'Profile updated successfully!' });
        setAdminData(prev => ({ ...prev, name: formData.name }));
        dispatch(updateSupportProfileSuccess(res.data.data));
        setEditInfo(false);
      }
    } catch (err) {
      setUpdateMessage({
        type: 'danger',
        text: err.response?.data?.msg || 'Failed to update profile.'
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loadingData) {
    return <Wrapper><div className="text-center p-5"><Spinner animation="border" /></div></Wrapper>;
  }

  return (
    <Wrapper>
      <div style={{ overflow: 'hidden', width: '100%', maxWidth: '100vw' }}>
        <Row className="g-4 m-0 justify-content-center">
          {/* PROFILE CARD */}
          <Col md={8} lg={6} className="px-2">
            <Card className="rounded-4 shadow-sm h-100 border-0">
              <Card.Body className="p-0">
                {/* HEADER */}
                <div className="d-flex align-items-center gap-3 p-4 border-bottom">
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 80, height: 80, fontSize: 32, fontWeight: 'bold' }}
                  >
                    {adminData?.name?.[0]?.toUpperCase() || 'S'}
                  </div>

                  <div className="flex-grow-1 overflow-hidden">
                    <h4 className="fw-bold mb-1 text-truncate">{adminData?.name || 'Super Admin'}</h4>
                    <p className="text-muted mb-0">Support / Operations Account</p>
                  </div>
                </div>

                {/* INFO */}
                <div className="p-4 bg-light position-relative rounded-bottom-4">
                  <div className="position-absolute top-0 end-0 m-3" style={{ cursor: 'pointer' }}>
                    {editInfo ? (
                      <FaTimes onClick={() => {
                        setEditInfo(false);
                        setUpdateMessage({ type: '', text: '' });
                      }} />
                    ) : (
                      <FaPencilAlt onClick={() => setEditInfo(true)} />
                    )}
                  </div>

                  {updateMessage.text && (
                    <Alert variant={updateMessage.type} dismissible onClose={() => setUpdateMessage({ type: '', text: '' })} className="mb-3">
                      {updateMessage.text}
                    </Alert>
                  )}

                  {editInfo ? (
                    <Form onSubmit={handleUpdateProfile}>
                      {/* --- NAME --- */}
                      <Form.Group className="mb-3">
                        <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                          <FaUser /> Full Name
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>

                      {/* --- EMAIL (Read Only) --- */}
                      <Form.Group className="mb-3">
                        <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                          <FaEnvelope /> Email Address
                        </Form.Label>
                        <Form.Control
                          type="email"
                          value={adminData?.email || ''}
                          disabled
                          style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                        />
                        <Form.Text className="text-muted">
                          Email cannot be changed. Contact system administrator if needed.
                        </Form.Text>
                      </Form.Group>

                      {/* --- PASSWORD --- */}
                      <Form.Group className="mb-4">
                        <Form.Label className="d-flex align-items-center gap-2 text-secondary">
                          <FaKey /> Set New Password
                        </Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Leave empty to keep current password"
                          autoComplete="new-password"
                        />
                        <Form.Text className="text-muted">
                          Optional. Enter a value only if you want to change it.
                        </Form.Text>
                      </Form.Group>

                      <div className="d-flex gap-2">
                        <Button type="submit" variant="primary" disabled={updating} className="flex-grow-1">
                          {updating ? <><Spinner size="sm" className="me-2" />Updating...</> : 'Save Changes'}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => {
                            setEditInfo(false);
                            setUpdateMessage({ type: '', text: '' });
                          }}
                          disabled={updating}
                        >
                          Cancel
                        </Button>
                      </div>
                    </Form>
                  ) : (
                    <>
                      <Row className="mb-3 g-2 mx-0 align-items-center">
                        <Col xs="auto" className="text-secondary"><FaUser /></Col>
                        <Col className="text-break pe-4 fw-medium">{adminData?.name}</Col>
                      </Row>
                      <Row className="mb-3 g-2 mx-0 align-items-center">
                         <Col xs="auto" className="text-secondary"><FaEnvelope /></Col>
                         <Col className="text-break pe-4 text-muted">{adminData?.email}</Col>
                      </Row>
                      <Row className="g-2 mx-0 align-items-center">
                        <Col xs="auto" className="text-secondary"><FaKey /></Col>
                        <Col className="text-break pe-4 text-muted">••••••••</Col>
                      </Row>
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </Wrapper>
  );
}

export default SupportProfile;